// REDUX
import { createSlice } from "@reduxjs/toolkit";
import { getRentalLogs, getSMSLogs } from "api/auditApi";
import dayjs from "dayjs";
import { t } from "i18next";

// MISC
import { IAuditInitialState } from "models/interfaces";

const initialState: IAuditInitialState = {
  rentalLogs: {
    content: [],
    pageable: {
      sort: {
        empty: false,
        sorted: false,
        unsorted: false,
      },
      offset: 0,
      pageNumber: 0,
      pageSize: 0,
      paged: false,
      unpaged: false,
    },
    totalPages: 0,
    totalElements: 0,
    last: false,
    size: 0,
    number: 0,
    sort: {
      empty: false,
      sorted: false,
      unsorted: false,
    },
    numberOfElements: 0,
    first: false,
    empty: false,
  },
  smsLogs: {
    content: [],
    pageable: {
      sort: {
        empty: false,
        sorted: false,
        unsorted: false,
      },
      offset: 0,
      pageNumber: 0,
      pageSize: 0,
      paged: false,
      unpaged: false,
    },
    totalPages: 0,
    totalElements: 0,
    last: false,
    size: 0,
    number: 0,
    sort: {
      empty: false,
      sorted: false,
      unsorted: false,
    },
    numberOfElements: 0,
    first: false,
    empty: false,
  },
  isLoading: false,
  errorMessage: "",
  rentalLogsTotalPages: 0,
  rentalsLogsTableData: [],
  rentalLogsFilters: {
    fullName: {
      usernameOrPhoneNumber: "",
    },
    lockerId: "",
    startDate: "",
    endDate: "",
    page: "",
  },
  smsTotalPages: 0,
  smsLogsTableData: [],
  smsLogsFilters: {
    fullName: {
      usernameOrPhoneNumber: "",
    },
    startDate: "",
    endDate: "",
    page: "",
  },
  selectedAuditReportType : ""
};

export const auditSlice = createSlice({
  name: "audit",
  initialState,
  reducers: {
    toggleRentalLogsFiltersFullNameState: (state, action) => {
      state.rentalLogsFilters.fullName = action.payload;
    },
    toggleRentalLogsFiltersLockerIdState: (state, action) => {
      state.rentalLogsFilters.lockerId = action.payload;
    },
    toggleRentalLogsFiltersStartDateState: (state, action) => {
      state.rentalLogsFilters.startDate = action.payload;
    },
    toggleRentalLogsFiltersEndDateState: (state, action) => {
      state.rentalLogsFilters.endDate = action.payload;
    },
    toggleRentalLogsFiltersPageState: (state, action) => {
      state.rentalLogsFilters.page = action.payload;
    },
    toggleClearRentallLogsFiltersState: (state) => {
      state.rentalLogsFilters = {
        fullName: {
          usernameOrPhoneNumber: "",
        },
        lockerId: "",
        startDate: "",
        endDate: "",
        page: "",
      };
    },
    toggleClearRentalsLogsTableData: (state) => {
      state.rentalsLogsTableData = null;
    },

    toggleSmsLogsFilterFullNameState: (state, action) => {
      state.smsLogsFilters.fullName = action.payload;
    },
    toggleSmsLogsFilterStartDateState: (state, action) => {
      state.smsLogsFilters.startDate = action.payload;
    },
    toggleSmsLogsFilterEndDateState: (state, action) => {
      state.smsLogsFilters.endDate = action.payload;
    },
    toggleSmsLogsFilterPageState: (state, action) => {
      state.smsLogsFilters.page = action.payload;
    },
    toggleClearSmsLogsFiltersState: (state) => {
      state.smsLogsFilters = {
        fullName: {
          usernameOrPhoneNumber: "",
        },
        startDate: "",
        endDate: "",
        page: "",
      };
    },
    toggleClearSmsLogsTableData: (state) => {
      state.smsLogsTableData = null;
    },
    toggleSelectedAuditReportType: (state, action) => {
      state.selectedAuditReportType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRentalLogs.pending, (state) => {
      state.isLoading = true;
      state.errorMessage = "";
    });
    builder.addCase(getRentalLogs.fulfilled, (state, action) => {
      state.isLoading = false;
      let data = action.payload.content;
      let initialData = Array.from({ length: data.length }, () => ({
        username: "",
        fullName: "",
        lockerName: "",
        status: "",
        date: "",
        startTime: "",
        rentalId: 0,
      }));
      data.forEach((item, index) => {
        initialData[index] = {
          username: item.rental.user.username,
          fullName: item?.rental?.user?.fullName || "NO_NAME_PROVIDED",
          lockerName: item.rental.locker.name,
          status: t(`audit.rentals.status.${item.status}`),
          date: dayjs(item.creationDate).format("DD/MM/YYYY"),
          startTime: dayjs(item.creationDate).format("HH:mm:ss"),
          rentalId: item.rental.id,
        };
      });
      state.rentalsLogsTableData = initialData;
      state.rentalLogsTotalPages = action.payload.totalPages;
    });
    builder.addCase(getRentalLogs.rejected, (state, action) => {
      state.isLoading = false;
      state.errorMessage = action.error.message;
    });
    builder.addCase(getSMSLogs.pending, (state) => {
      state.isLoading = true;
      state.errorMessage = "";
    });
    builder.addCase(getSMSLogs.fulfilled, (state, action) => {
      state.isLoading = false;
      let data = action.payload.content;
      let initialData = Array.from({ length: data.length }, () => ({
        phoneNumber: "",
        fullName: "",
        imei: "",
        date: "",
        startTime: "",
        id: 0,
      }));
      data.forEach((item, index) => {
        initialData[index] = {
          phoneNumber: item.phoneNumber,
          fullName: item?.user?.fullName || "NO_NAME_PROVIDED",
          imei: item.imei,
          date: dayjs(item.creationDate).format("DD/MM/YYYY"),
          startTime: dayjs(item.creationDate).format("HH:mm:ss"),
          id: item.user.id,
        };
      });
      state.smsLogsTableData = initialData;
      state.smsTotalPages = action.payload.totalPages;
    });
    builder.addCase(getSMSLogs.rejected, (state, action) => {
      state.isLoading = false;
      state.errorMessage = action.error.message;
    });
  },
});

export const {
  toggleRentalLogsFiltersFullNameState,
  toggleRentalLogsFiltersLockerIdState,
  toggleRentalLogsFiltersStartDateState,
  toggleRentalLogsFiltersEndDateState,
  toggleRentalLogsFiltersPageState,
  toggleClearRentallLogsFiltersState,
  toggleClearRentalsLogsTableData,
  toggleSmsLogsFilterFullNameState,
  toggleSmsLogsFilterStartDateState,
  toggleSmsLogsFilterEndDateState,
  toggleSmsLogsFilterPageState,
  toggleClearSmsLogsFiltersState,
  toggleClearSmsLogsTableData,
  toggleSelectedAuditReportType
} = auditSlice.actions;

export const rentalLogsTotalPages = (state) => state.audit.rentalLogsTotalPages;
export const rentalsLogsData = (state) => state.audit.rentalsLogsTableData;
export const rentalLogsFilters = (state) => state.audit.rentalLogsFilters;
export const smsLogsTotalPages = (state) => state.audit.smsTotalPages;
export const smsLogsData = (state) => state.audit.smsLogsTableData;
export const smsLogsFilters = (state) => state.audit.smsLogsFilters;
export const selectedAuditReportTypes = (state) =>
  state.audit.selectedAuditReportType;

export default auditSlice.reducer;
